import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { subscriptionService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { IFeatureFlagsResponse } from "@miplatform/types";
import { bugsnag } from "@miplatform/utils";

type FeatureFlagContextProperties = {
  featureFlags: IFeatureFlagsResponse | null;
};

const FeatureFlagContext = createContext<
  FeatureFlagContextProperties | undefined
>(undefined);

export const FeatureFlagProvider: FC<PropsWithChildren> = ({ children }) => {
  const [featureFlags, setFeatureFlags] =
    useState<IFeatureFlagsResponse | null>(null);

  const value = useMemo(() => ({ featureFlags }), [featureFlags]);

  useEffect(() => {
    const getFlags = async () => {
      try {
        const response =
          await subscriptionService.tenantConfig.getFeatureFlags();
        if (response.status === HTTP_STATUSES.OK) {
          setFeatureFlags(response.data);
        }
      } catch (error) {
        bugsnag.notify(error);
      }
    };
    getFlags();
  }, []);

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlagsContext = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error(
      "useFeatureFlagContext must be used within a FeatureFlagProvider"
    );
  }

  return context;
};
